import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import SearchOffIcon from '@mui/icons-material/SearchOff';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { HostingSitesV2200ItemsItem } from '@newfold/huapi-js/src/index.schemas';

import { getShortDescription } from '~/Nova/scenes/HostingList/utils/helpers';
import { PublicImageItem } from '~/Nova/scenes/HostingList/utils/types';
import EmptySectionCard from '~/Nova/scenes/Site/components/EmptySectionCard';

import SelectableListCard from '../../../components/SelectableListCard';
import { CPANEL_APP } from '../../../utils/constants';
import { getImageLinks, getLogoConfig } from '../../../utils/imageLogos';
import ReImageAppsModal from './ReImageAppsModal';

interface ReImageServerAppsProps {
  installedAppData?: HostingSitesV2200ItemsItem;
  osAdvancedImages?: PublicImageItem[];
  images?: PublicImageItem[];
  hostingId: string;
  isCpanelInstalled?: boolean;
  nonDockerAppName: string;
  isSelfManagedDedi?: boolean;
  searchQuery?: string;
}

const ReImageServerApps = ({
  installedAppData = undefined,
  hostingId,
  isCpanelInstalled = false,
  osAdvancedImages = [],
  images = undefined,
  nonDockerAppName,
  isSelfManagedDedi = false,
  searchQuery = '',
}: ReImageServerAppsProps) => {
  const { t } = useTranslation('hosting', { keyPrefix: 'hostingNova' });
  const { t: tSelfManagedHosting } = useTranslation('selfManagedHosting');
  const [openSelfManagedAppsModal, setOpenSelfManagedAppsModal] =
    useState(false);
  const [selectedApp, setSelectedApp] = useState('');
  const { status: appStatus = '' } = installedAppData || {};
  const isAppActive = appStatus === 'active';

  const apps =
    images ??
    osAdvancedImages.filter(
      (img) =>
        img.type === 'docker_app' ||
        img.type === 'app' ||
        img.type === 'cpanel',
    );

  const handleAppAction = (selectedApp: string) => {
    setOpenSelfManagedAppsModal(true);
    setSelectedApp(selectedApp);
  };
  return (
    <>
      {apps.length ? (
        <Stack spacing={2}>
          {apps.map((image) => {
            const { id, app, software, links, description, title } = image;
            const appKey = app ?? software ?? '';
            const { docsUrl, communityUrl } = getImageLinks(links);
            const logoConfig = getLogoConfig(appKey);
            const shortDescription = getShortDescription(description ?? '');

            const isCurrentlyInstalled =
              (installedAppData?.subtype === appKey && isAppActive) ||
              (!installedAppData &&
                ((appKey === CPANEL_APP && isCpanelInstalled) ||
                  appKey === nonDockerAppName));

            return (
              <SelectableListCard
                key={`${appKey}-${id}`}
                app={appKey}
                title={title ?? t(`selfManagedApps.apps.${appKey}.title`)}
                description={description ?? ''}
                shortDescription={shortDescription}
                icon={logoConfig?.logo}
                buttonText={t('selfManagedApps.selectBtn')}
                onSelect={handleAppAction}
                testId={`self-managed-card-${appKey}`}
                docsUrl={docsUrl}
                communityUrl={communityUrl}
                showCurrentServerImageOrApp={isCurrentlyInstalled}
              />
            );
          })}
        </Stack>
      ) : (
        searchQuery && (
          <EmptySectionCard>
            <IconButton disableRipple>
              <SearchOffIcon sx={{ fontSize: 46, color: '#244b9c' }} />
            </IconButton>
            <Stack spacing={1} alignItems="center">
              <Typography variant="h3">
                {tSelfManagedHosting('reimageSearch.emptyState.title')}
              </Typography>

              <Typography variant="body1">
                {tSelfManagedHosting('reimageSearch.emptyState.description')}
              </Typography>
            </Stack>
          </EmptySectionCard>
        )
      )}
      <ReImageAppsModal
        open={openSelfManagedAppsModal}
        setOpen={setOpenSelfManagedAppsModal}
        selectedApp={selectedApp}
        hostingId={hostingId}
        osAdvancedImages={osAdvancedImages}
        isSelfManagedDedi={isSelfManagedDedi}
      />
    </>
  );
};

export default ReImageServerApps;
